import { useMemo, useState } from "react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { FieldRow } from "./courses";
import { useConfig, usePlacementAssessments, useSaveConfig } from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import { SlidersHorizontal } from "lucide-react";
import { VideoUploadField } from "@/components/common/video-upload-field";

export function ConfigPage() {
  const { data, isLoading, isError, refetch } = useConfig();
  const { data: assessments } = usePlacementAssessments();
  const save = useSaveConfig();
  const toast = useToast();

  const initial = useMemo(() => {
    const map: Record<string, string> = {};
    for (const entry of data ?? []) {
      const raw = entry.value;
      map[entry.key] =
        raw === null || raw === undefined
          ? ""
          : typeof raw === "string"
            ? raw
            : JSON.stringify(raw);
    }
    return map;
  }, [data]);

  const [form, setForm] = useState<Record<string, string>>({});
  const merged = { ...initial, ...form };

  const set = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const onSave = async (key: string) => {
    try {
      const raw = merged[key] ?? "";
      let value: unknown = raw;
      if (raw.trim().startsWith("{") || raw.trim().startsWith("[")) {
        try {
          value = JSON.parse(raw);
        } catch {
          // keep as string if JSON invalid
        }
      }
      await save.mutateAsync({ key, value });
      toast.success("Config saved", key);
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <PageShell title="Config" description="Intro media, placement defaults, and feature toggles.">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState title="Couldn't load config" onRetry={() => void refetch()} />
      ) : !data ? (
        <EmptyState icon={SlidersHorizontal} title="No config entries yet" />
      ) : (
        <div className="space-y-5">
          <ConfigSection title="Intro media">
            <FieldRow
              label="Intro video URL"
              id="center_intro_video"
              hint="Sent to leads right after they share a phone number. Upload a file or paste a URL."
            >
              <div className="space-y-2">
                <VideoUploadField
                  id="center_intro_video"
                  endpoint="/admin-upload/intro-video"
                  value={merged.center_intro_video ?? ""}
                  onChange={(v) => set("center_intro_video", v)}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onSave("center_intro_video")}
                    disabled={save.isPending}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </FieldRow>
            <ConfigField
              label="Intro caption"
              id="intro_caption_custom"
              value={merged.intro_caption_custom ?? ""}
              onChange={(v) => set("intro_caption_custom", v)}
              onSave={() => onSave("intro_caption_custom")}
              textarea
            />
          </ConfigSection>

          <ConfigSection title="Placement defaults" hint="Map subject → assessment used by the bot when no overrides apply.">
            <ConfigField
              label="Math assessment ID"
              id="placement_math_assessment_id"
              value={merged.placement_math_assessment_id ?? ""}
              onChange={(v) => set("placement_math_assessment_id", v)}
              onSave={() => onSave("placement_math_assessment_id")}
              hint={assessmentHint(assessments, "Math")}
            />
            <ConfigField
              label="English assessment ID"
              id="placement_english_assessment_id"
              value={merged.placement_english_assessment_id ?? ""}
              onChange={(v) => set("placement_english_assessment_id", v)}
              onSave={() => onSave("placement_english_assessment_id")}
              hint={assessmentHint(assessments, "English")}
            />
          </ConfigSection>

          <ConfigSection title="All entries" hint="Raw view, advanced.">
            <div className="rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="border-b border-line bg-surface-muted text-left">
                  <tr>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-ink-muted">Key</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-wider text-ink-muted">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry) => (
                    <tr key={entry.key} className="border-b border-line">
                      <td className="px-4 py-2 font-mono text-xs text-ink">{entry.key}</td>
                      <td className="px-4 py-2 font-mono text-xs text-ink-muted truncate max-w-md">
                        {typeof entry.value === "string"
                          ? entry.value
                          : JSON.stringify(entry.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ConfigSection>
        </div>
      )}
    </PageShell>
  );
}

function ConfigSection({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h3 className="text-md font-semibold text-ink">{title}</h3>
      {hint && <p className="mt-1 text-sm text-ink-muted">{hint}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function ConfigField({
  label,
  id,
  value,
  onChange,
  onSave,
  hint,
  textarea,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  hint?: string;
  textarea?: boolean;
}) {
  return (
    <FieldRow label={label} id={id} hint={hint}>
      <div className="flex gap-2">
        {textarea ? (
          <Textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
          />
        ) : (
          <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
        )}
        <Button variant="outline" onClick={onSave} className="self-start">
          Save
        </Button>
      </div>
    </FieldRow>
  );
}

function assessmentHint(
  assessments: Array<{ id: string; name: string; subject?: string }> | undefined,
  subject: string
): string {
  if (!assessments) return "";
  const match = assessments.find((a) => (a.subject || "").toLowerCase() === subject.toLowerCase());
  return match ? `e.g. ${match.id} (${match.name})` : "";
}
